/**
 * Collapsible element with a heading and variable content
 */
class Collapsible {
    /**
     * Constructor
     *
     * @param {HTMLElement} collapsibleElement - The collapsible element.
     */
    constructor(collapsibleElement) {
        // The collapsible DOM element
        this.collapsible = collapsibleElement;
        this.collapsibleId = collapsibleElement.id;

        // The heading DOM element
        const headingElement = this.collapsible.querySelector(`#${this.collapsibleId}-heading`);
        if (!headingElement) {
            return;
        }

        // The button that controls the collapsible
        this.button = document.createElement('button');
        // Setting button attributes and properties
        const isOpen = this.button.getAttribute('aria-expanded') ? 'true' : 'false';
        this.button.setAttribute('aria-expanded', isOpen);
        this.button.classList.add('CollapsibleButton');

        // Store button content and collapsible content as instance variables
        let buttonContent = headingElement.innerHTML;
        this.collapsibleContent = this.collapsible.querySelector(`#${this.collapsibleId}-panel`);

        // Check for collapsible content and its ID
        this.collapsibleContentId = this.collapsibleContent.getAttribute('id');
        // Handle cases depending on the collapsible content having an id
        if (this.collapsibleContentId) {
            this.button.setAttribute('aria-controls', this.collapsibleContentId);
        } else {
            this.fallback = headingElement.querySelector('[id]');
            if (this.fallback) {
                this.fallbackId = this.fallback.getAttribute('id');
                headingElement.querySelector('[id]').removeAttribute('id');
                buttonContent = headingElement.innerHTML;
                headingElement.setAttribute('id', `${this.fallbackId}-heading`);
                this.button.setAttribute('aria-controls', this.fallbackId);
                this.collapsibleContent.setAttribute('id', this.fallbackId);
            }
        }

        // Set button innerHTML and replace heading content
        this.button.innerHTML = buttonContent;
        headingElement.innerHTML = '';
        headingElement.appendChild(this.button);

        // Attach click event listener to the button to handle opening and closing
        headingElement.addEventListener('click', () => this.toggle());
    }

    // Method to open the collapsible
    open() {
        this.collapsible.classList.add('Collapsible--open');
        this.collapsible.classList.remove('Collapsible--closed');
        if (this.button) {
            this.button.setAttribute('aria-expanded', 'true');
        }
    }

    // Method to close the collapsible
    close() {
        this.collapsible.classList.add('Collapsible--closed');
        this.collapsible.classList.remove('Collapsible--open');
        if (this.button) {
            this.button.setAttribute('aria-expanded', 'false');
        }
    }

    // Toggle method to switch between open and closed states
    toggle() {
        const isOpen = this.button.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
            this.close();
        } else {
            this.open();
        }
    }
}

// Initialize all collapsibles once the DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const collapsibleElements = document.querySelectorAll('.Collapsible');
    collapsibleElements.forEach((element) => {
        // eslint-disable-next-line no-new
        new Collapsible(element);
        element.classList.remove('Collapsible--init');
    });
});
